import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import yitLogo from '../../assets/yitLogo.png';
import { APP_VERSION_NUMBER } from '../../enums';
import { useIsDesktopDevice } from '../../hooks/useIsDesktopDevice';
import { salesWhatsappNumber } from '../../constants';

const keyframesStyle = `
  @keyframes splashUp { 0% { opacity: 0; transform: translateY(20px); } 100% { opacity: 1; transform: translateY(0); } }
  @keyframes splashDot { 0%, 100% { opacity: .4; } 50% { opacity: 1; } }
  @keyframes splashLogo { 0% { opacity: 0; transform: scale(.4) translateY(10px); } 55% { opacity: 1; transform: scale(1.12) translateY(0); } 75% { transform: scale(.97); } 100% { opacity: 1; transform: scale(1); } }
  @keyframes splashFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
  @keyframes splashRing { 0% { opacity: .5; transform: scale(.6); } 100% { opacity: 0; transform: scale(1.9); } }
  @keyframes splashMarquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
`;

const Splash: React.FC = () => {
    const { t } = useTranslation();
    const isDesktop = useIsDesktopDevice();
    const whatsappHref = `https://wa.me/${salesWhatsappNumber}?text=${encodeURIComponent(t('splash.whatsappMessage'))}`;
    const tickerText = `${t('splash.ticker')} `;

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                overflow: 'hidden',
                bgcolor: '#f5f7f8',
                fontFamily: "'Heebo', sans-serif",
                direction: 'rtl',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <style>{keyframesStyle}</style>

            <Box
                sx={{
                    position: 'absolute',
                    top: '-16%',
                    left: '-10%',
                    width: '46%',
                    height: '60%',
                    borderRadius: '999px',
                    background: 'radial-gradient(circle, rgba(38,165,209,.16) 0%, rgba(38,165,209,0) 68%)',
                    pointerEvents: 'none',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '-14%',
                    right: '-8%',
                    width: '40%',
                    height: '52%',
                    borderRadius: '999px',
                    background: 'radial-gradient(circle, rgba(255,210,31,.2) 0%, rgba(255,210,31,0) 68%)',
                    pointerEvents: 'none',
                }}
            />

            {/* top bar */}
            <Box
                sx={{
                    position: 'relative',
                    flex: '0 0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    p: isDesktop ? '16px 40px' : '14px 20px',
                    bgcolor: '#26a5d1',
                    color: '#eafaff',
                    fontSize: isDesktop ? 14 : 12,
                    fontWeight: 600,
                }}
            >
                <Box
                    sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '999px',
                        bgcolor: '#ffd21f',
                        animation: 'splashDot 1.6s ease-in-out infinite',
                        flexShrink: 0,
                    }}
                />
                <span>{t('splash.topBar')}</span>
            </Box>

            {/* hero */}
            <Box
                sx={{
                    position: 'relative',
                    flex: '1 1 auto',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: 'center',
                    p: isDesktop ? '40px 56px' : '20px 24px',
                    gap: isDesktop ? '26px' : '16px',
                    overflow: 'auto',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: isDesktop ? '150px' : '64px',
                        height: isDesktop ? '150px' : '64px',
                        mt: isDesktop ? '10px' : '24px',
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            borderRadius: '999px',
                            border: '1.5px solid rgba(38,165,209,.4)',
                            animation: 'splashRing 2.6s ease-out infinite',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            borderRadius: '999px',
                            border: '1.5px solid rgba(38,165,209,.4)',
                            animation: 'splashRing 2.6s ease-out infinite',
                            animationDelay: '1.3s',
                        }}
                    />
                    <Box sx={{ animation: 'splashLogo 1s cubic-bezier(.18,1.1,.3,1) both' }}>
                        <Box
                            component="img"
                            src={yitLogo}
                            alt="YIT"
                            sx={{
                                animation: 'splashFloat 4.5s ease-in-out infinite 1s',
                                height: isDesktop ? '118px' : '56px',
                                width: 'auto',
                                display: 'block',
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    component="span"
                    sx={{
                        animation: 'splashUp .6s ease .16s both',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: isDesktop ? 15 : 12,
                        fontWeight: 800,
                        letterSpacing: '.2em',
                        color: '#1b87b0',
                    }}
                >
                    {t('splash.eyebrow')}
                </Box>

                <Box
                    component="h1"
                    sx={{
                        animation: 'splashUp .7s ease .22s both',
                        m: 0,
                        color: '#1c2b33',
                        fontWeight: 900,
                        fontSize: isDesktop ? 'clamp(58px, 6.4vw, 86px)' : 34,
                        lineHeight: 0.98,
                        letterSpacing: '-.02em',
                    }}
                >
                    {t('splash.titleLine1')}
                    <br />
                    <Box component="span" sx={{ color: '#26a5d1' }}>
                        {t('splash.titleLine2')}
                    </Box>
                </Box>

                <Box
                    component="p"
                    sx={{
                        animation: 'splashUp .7s ease .34s both',
                        m: 0,
                        maxWidth: '34em',
                        color: '#5c6d76',
                        fontSize: isDesktop ? 20 : 14,
                        lineHeight: 1.5,
                    }}
                >
                    {t('splash.subtitle')}
                </Box>

                <Box
                    sx={{
                        animation: 'splashUp .7s ease .46s both',
                        width: '100%',
                        maxWidth: '52em',
                        overflow: 'hidden',
                        WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)',
                        maskImage: 'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            width: 'max-content',
                            gap: '24px',
                            animation: 'splashMarquee 22s linear infinite',
                            direction: 'ltr',
                        }}
                    >
                        {[0, 1].map((i) => (
                            <Box
                                key={i}
                                component="span"
                                sx={{
                                    display: 'flex',
                                    gap: '24px',
                                    whiteSpace: 'nowrap',
                                    color: '#aebdc4',
                                    fontWeight: 800,
                                    fontSize: isDesktop ? 20 : 14,
                                    letterSpacing: '.02em',
                                }}
                            >
                                {tickerText}
                            </Box>
                        ))}
                    </Box>
                </Box>

                <Box
                    sx={{
                        animation: 'splashUp .7s ease .58s both',
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: '14px',
                        width: '100%',
                        maxWidth: '34em',
                    }}
                >
                    <Box
                        component="a"
                        href={whatsappHref}
                        target="_blank"
                        rel="noopener"
                        sx={{
                            flex: 1,
                            minWidth: '12em',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            bgcolor: '#ffd21f',
                            color: '#1c2b33',
                            textDecoration: 'none',
                            fontWeight: 800,
                            fontSize: isDesktop ? 18 : 14,
                            p: isDesktop ? '17px 26px' : '14px 20px',
                            borderRadius: '14px',
                            boxShadow: '0 12px 26px rgba(255,210,31,.4)',
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.5 14.4c-.3-.15-1.7-.85-2-.95-.26-.1-.45-.15-.65.15-.19.28-.74.94-.9 1.13-.17.19-.34.21-.63.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.04-.17-.29-.02-.45.13-.6.13-.13.3-.34.44-.51.15-.17.2-.29.3-.48.1-.2.05-.36-.02-.5-.08-.15-.65-1.57-.9-2.15-.24-.57-.48-.49-.65-.5h-.56c-.19 0-.5.07-.77.36-.26.28-1 .98-1 2.4s1.03 2.78 1.17 2.97c.14.19 2.02 3.08 4.9 4.32.68.29 1.22.47 1.63.6.69.22 1.31.19 1.8.11.55-.08 1.7-.69 1.94-1.36.24-.67.24-1.24.17-1.36-.07-.12-.26-.19-.55-.34ZM12 2a10 10 0 0 0-8.5 15.28L2 22l4.85-1.27A10 10 0 1 0 12 2Z" />
                        </svg>
                        <span>{t('splash.ctaWhatsapp')}</span>
                    </Box>
                    <Box
                        component="a"
                        href="https://y-it.co.il/"
                        target="_blank"
                        rel="noopener"
                        sx={{
                            flex: 1,
                            minWidth: '12em',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            bgcolor: 'transparent',
                            color: '#1b87b0',
                            textDecoration: 'none',
                            fontWeight: 700,
                            fontSize: isDesktop ? 18 : 14,
                            p: isDesktop ? '17px 26px' : '14px 20px',
                            borderRadius: '14px',
                            border: '1.5px solid #bfdcea',
                        }}
                    >
                        {t('splash.ctaWebsite')}
                    </Box>
                </Box>
            </Box>

            {/* footer */}
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    direction: 'ltr',
                    p: isDesktop ? '18px 40px' : '14px 20px',
                    borderTop: '1px solid #e2e8eb',
                    color: '#9aa8b0',
                    fontSize: isDesktop ? 12 : 10,
                    flexShrink: 0,
                }}
            >
                <Box component="img" src={yitLogo} alt="YIT" sx={{ height: isDesktop ? '26px' : '20px', width: 'auto' }} />
                <span>Ver {APP_VERSION_NUMBER}</span>
            </Box>
        </Box>
    );
};

export default Splash;
